import { clone } from 'lodash-es';

/**
 * @typedef {Object} AdvancedHelper
 * @property {string}                label
 * @property {AdvancedHelperBlock[]} blocks
 */

/**
 * @typedef {AdvancedHelperTextBlock | AdvancedHelperImageBlock} AdvancedHelperBlock
 */

/**
 * @typedef {Object} AdvancedHelperTextBlock
 * @property {"TEXT"} type
 * @property {string} contents
 */

/**
 * @typedef {Object} AdvancedHelperImageBlock
 * @property {"IMAGE"}                    type
 * @property {AdvancedHelperImageContents} contents
 */

/**
 * @typedef {Object} AdvancedHelperImageContents
 * @property {string} src - Data URL with base64-encoded image (e.g. "data:image/png;base64,...")
 */

export default class Parameter
{
    // Parameter properties
    code                   = null;
    data_section           = null;
    default_value          = null;
    force_defined_value    = false;
    defined_value          = null;
    defined_value_label    = null;
    form_display           = null;
    form_label             = null;
    form_splitted_display  = null;
    formatters             = null;
    grouping_key           = null;
    has_access             = null;
    help                   = null;
    /** @type {?AdvancedHelper} */
    advanced_helper        = null;
    is_enabled             = null;
    is_grouped             = null;
    is_required            = null;
    list_items             = [];
    suggested_items        = [];
    max                    = null;
    min                    = null;
    nb_decimals            = null;
    note                   = null;
    prefix                 = null;
    suffix                 = null;
    priced_article_code    = null;
    required_article_codes = [];
    section                = null;
    tab                    = null;
    line                   = null;
    /** @type {?number} */
    sort_order             = null;
    step                   = null;
    unit                   = null;
    value_type             = null;

    value = null;

    oldValue = null; // Value is copied here after every template() call.

    error = null;

    // ------------------------------------------------------------ CONSTRUCTOR

    /**
     * Construct a new Parameter object.
     *
     * @param {Object}          [properties]
     * @param {?string}         [properties.code]
     * @param {?string}         [properties.data_section]
     * @param {any}             [properties.default_value]
     * @param {boolean}         [properties.force_defined_value]
     * @param {any}             [properties.defined_value]
     * @param {?string}         [properties.defined_value_label]
     * @param {?string}         [properties.form_display]
     * @param {?string}         [properties.form_label]
     * @param {?string}         [properties.form_splitted_display]
     * @param {?string}         [properties.formatters]
     * @param {?string}         [properties.grouping_key]
     * @param {?boolean}        [properties.has_access]
     * @param {?string}         [properties.help]
     * @param {?AdvancedHelper} [properties.advanced_helper]
     * @param {?boolean}        [properties.is_enabled]
     * @param {?boolean}        [properties.is_grouped]
     * @param {?boolean}        [properties.is_required]
     * @param {Object[]}        [properties.list_items]
     * @param {Object[]}        [properties.suggested_items]
     * @param {?number}         [properties.max]
     * @param {?number}         [properties.min]
     * @param {?number}         [properties.nb_decimals]
     * @param {?string}         [properties.note]
     * @param {?string}         [properties.prefix]
     * @param {?string}         [properties.suffix]
     * @param {?string}         [properties.priced_article_code]
     * @param {Array}           [properties.required_article_codes]
     * @param {?string}         [properties.section]
     * @param {?string}         [properties.tab]
     * @param {?string}         [properties.line]
     * @param {?number}         [properties.sort_order]
     * @param {?number}         [properties.step]
     * @param {?string}         [properties.unit]
     * @param {?string}         [properties.value_type]
     * @param {any}             [properties.value]
     * @param {any}             [properties.oldValue]
     * @param {?string}         [properties.error]
     */
    constructor(properties = {})
    {
        this.setProperties(properties);
    }

    // ------------------------------------------------------------ PARAMETER PROPERTIES

    /**
     * Set the properties
     * @param {Object}          [properties]
     * @param {?string}         [properties.code]
     * @param {?string}         [properties.data_section]
     * @param {any}             [properties.default_value]
     * @param {boolean}         [properties.force_defined_value]
     * @param {any}             [properties.defined_value]
     * @param {?string}         [properties.defined_value_label]
     * @param {?string}         [properties.form_display]
     * @param {?string}         [properties.form_label]
     * @param {?string}         [properties.form_splitted_display]
     * @param {?string}         [properties.formatters]
     * @param {?string}         [properties.grouping_key]
     * @param {?boolean}        [properties.has_access]
     * @param {?string}         [properties.help]
     * @param {?AdvancedHelper} [properties.advanced_helper]
     * @param {?boolean}        [properties.is_enabled]
     * @param {?boolean}        [properties.is_grouped]
     * @param {?boolean}        [properties.is_required]
     * @param {Array}           [properties.list_items]
     * @param {Array}           [properties.suggested_items]
     * @param {?number}         [properties.max]
     * @param {?number}         [properties.min]
     * @param {?number}         [properties.nb_decimals]
     * @param {?string}         [properties.note]
     * @param {?string}         [properties.prefix]
     * @param {?string}         [properties.suffix]
     * @param {?string}         [properties.priced_article_code]
     * @param {Array}           [properties.required_article_codes]
     * @param {?string}         [properties.section]
     * @param {?string}         [properties.tab]
     * @param {?string}         [properties.line]
     * @param {?number}         [properties.sort_order]
     * @param {?number}         [properties.step]
     * @param {?string}         [properties.unit]
     * @param {?string}         [properties.value_type]
     * @param {any}             [properties.value]
     * @param {any}             [properties.oldValue]
     * @param {?string}         [properties.error]
     */
    setProperties(properties)
    {
        const oldDefaultValue = this.default_value;

        // If value must be reset
        if(properties.reset_value)
        {
            this.setValue(null);
        }

        for(const key in properties)
        {
            if(this[key] === undefined)
            {
                continue;
            }

            this[key] = properties[key];

            // If current property is "list_items" and is not empty.
            if(
                key === 'list_items' && this[key].length &&
                // list_items is only an exhaustive value set for SELECTOR.
                // OPEN_SELECTOR only uses suggestions, so any value is valid.
                this.form_display !== 'OPEN_SELECTOR'
            )
            {
                const enabledItems = [];

                // Check that the current parameter value exists in list items
                // and that is not equal to a disabled item. If not found or
                // equal to a disabled item, set current value to NULL.
                let found = false;

                for(const item of properties[key])
                {
                    if(item.is_enabled)
                    {
                        enabledItems.push(item);
                    }

                    if(item.ovalue == this.value && item.is_enabled)
                    {
                        found = true;
                        break;
                    }
                };

                if(!found) // If current value was not found in enabled items => reset it.
                {
                    this.setValue(null);
                }

                if(enabledItems.length === 1 && properties.is_required)
                {
                    // Required field with only one enabled item => select it.
                    this.setValue(enabledItems[0].ovalue);
                }
            }

            // If a "defined value" is given, force replacement of value.
            if(key === 'defined_value' && this.force_defined_value)
            {
                this.setValue(properties[key], true);
            }

            // If a "default value" is given and value is "NULL", set value with default value.
            if(key === 'default_value' && this.value === null && properties[key] !== null)
            {
                this.setValue(properties[key]);
            }
        }

        // If default value has changed and current value is equal to old default value
        // => Set value to new default value
        if(this.value === oldDefaultValue)
        {
            this.setValue(this.default_value);
        }
    }

    // ------------------------------------------------------------ VALUE

    setValue(value)
    {
        if(value === null || value === '' || value === ' ')
        {
            this.value = null;
        }
        else
        {
            this.value = this.castValue(value);
        }
    }

    getValue()
    {
        return this.value;
    }

    castValue(value)
    {
        switch(this.value_type)
        {
            case 'boolean':
                return !!value;

            case 'integer':
                return parseInt(value);

            case 'float':
                return parseFloat(value);

            case 'string':
                return `${value}`;

            case 'json':
                return value;

            default:
                return value;
        }
    }

    // ------------------------------------------------------------ OLD VALUE

    setOldValueFromValue()
    {
        if(typeof this.value === 'object')
        {
            this.oldValue = clone(this.value);
        }
        else
        {
            this.oldValue = this.value;
        }
    }

    getOldValue()
    {
        return this.oldValue;
    }

    // ------------------------------------------------------------ DEFAULT VALUE

    getDefaultValue()
    {
        return this.default_value;
    }

    // ------------------------------------------------------------ SECTION

    getSectionCode()
    {
        return this.section ?? 'NONE';
    }

    // ------------------------------------------------------------ LINE

    getLine()
    {
        return this.line;
    }

    // ------------------------------------------------------------ ERROR

    setError(error)
    {
        this.error = error;
    }

    getError()
    {
        return this.error;
    }

    clearError()
    {
        this.error = null;
    }
}
